import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { Gear, Image, Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { UploadService } from '../../../../service/UploadService';
import { useFormBuilder } from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import ColorField from '../FormCustomization/ColorField';

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  fileUrl?: string;
  response?: any;
  isExtraction: boolean;
}

const FormSchema = z.object({
  successMessage: z.string().default(''),
  loadingMessage: z.string().default(''),
  agentSessionMode: z.boolean().default(false),
  backgroundColor: z.string().default('#ffffff'),
  backgroundImage: z.string().optional(),
});

export function FormSettings() {
  const { dispatch, state } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const settings = state.form.formSettings;
  const styles = state.form.formStyle;

  const formsMethods = useForm({
    defaultValues: {
      successMessage: settings?.successMessage ? settings.successMessage : '',
      loadingMessage: settings?.loadingMessage ? settings.loadingMessage : '',
      agentSessionMode: settings?.agentSessionMode ? settings.agentSessionMode : false,
      backgroundColor: styles?.backgroundColor ? styles.backgroundColor : '#ffffff',
      backgroundImage: styles?.backgroundImage ? styles.backgroundImage : '',
    },
    resolver: zodResolver(FormSchema),
  });

  useEffect(() => {
    formsMethods.reset({
      successMessage: settings?.successMessage ? settings.successMessage : '',
      loadingMessage: settings?.loadingMessage ? settings.loadingMessage : '',
      agentSessionMode: settings?.agentSessionMode ? settings.agentSessionMode : false,
      backgroundColor: styles?.backgroundColor ? styles.backgroundColor : '#ffffff',
      backgroundImage: styles?.backgroundImage ? styles.backgroundImage : '',
    });
  }, [state.form.formStyle, formsMethods]);

  const handleFormChange = (data: any) => {
    // Update form settings
    dispatch({
      type: ActionTypes.UPDATE_FORM_SETTINGS,
      payload: {
        successMessage: data.successMessage,
        loadingMessage: data.loadingMessage,
        agentSessionMode: data.agentSessionMode,
      },
    });

    // Update form styles
    dispatch({
      type: ActionTypes.UPDATE_FORM_STYLES,
      payload: {
        backgroundColor: data.backgroundColor,
        backgroundImage: data.backgroundImage,
      },
    });
  };

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        handleFormChange(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, state.form.formStyle]);

  const formValues = formsMethods.watch();

  const uploadFile = async (fileData: UploadedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f => {
            if (f.id === fileData.id && f.progress < 90) {
              return { ...f, progress: f.progress + 10 };
            }
            return f;
          }),
        );
      }, 500);

      const response = await UploadService.uploadFile(formData);

      clearInterval(progressInterval);

      setUploadedFiles(prev =>
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              status: 'complete' as const,
              progress: 100,
              fileUrl: response.data.src,
              response: response,
            };
          }
          return f;
        }),
      );

      if (response.data.src) {
        formsMethods.setValue('backgroundImage', response.data.src);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadedFiles(prev =>
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              status: 'error' as const,
              error: error instanceof Error ? error.message : 'Upload failed',
            };
          }
          return f;
        }),
      );
    }
  };

  const handleRemoveImage = () => {
    formsMethods.setValue('backgroundImage', '');
    setUploadedFiles([]);
  };

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    const newFiles = acceptedFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      progress: 0,
      status: 'uploading' as const,
      isExtraction: false,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    for (const fileData of newFiles) {
      await uploadFile(fileData);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    },
    onDrop,
  });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 0px 0 16px',
        }}
      >
        <Gear size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Form Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0', marginTop: '8px' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                    fontStyle: 'normal',
                  }}
                >
                  Success Message
                </Typography>
                <Controller
                  name="successMessage"
                  control={formsMethods.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="Enter success message..."
                      fullWidth
                      variant="outlined"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          margin: '0px',
                          fontSize: '14px',
                          height: '38px',
                        },
                      }}
                    />
                  )}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                    fontStyle: 'normal',
                  }}
                >
                  Loading Message
                </Typography>
                <Controller
                  name="loadingMessage"
                  control={formsMethods.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="Enter loading message..."
                      fullWidth
                      variant="outlined"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          margin: '0px',
                          fontSize: '14px',
                          height: '38px',
                        },
                      }}
                    />
                  )}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Controller
                  name="agentSessionMode"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            color: '#475467',
                          }}
                        >
                          Agent Session Mode
                        </Typography>
                      }
                    />
                  )}
                />
              </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Controller
                name="backgroundColor"
                control={formsMethods.control}
                render={({ field }) => (
                  <ColorField
                    label="Background Color"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="#ffffff"
                  />
                )}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                    fontStyle: 'normal',
                  }}
                >
                  Background Image
                </Typography>

                {!formValues?.backgroundImage ? (
                  <Controller
                    name="backgroundImage"
                    control={formsMethods.control}
                    render={({ field, fieldState }) => (
                      <div
                        style={{
                          border: '1px dashed #667085',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          color: '#98A2B3',
                          width: '100%',
                          minHeight: '120px',
                        }}
                      >
                        <div
                          {...getRootProps({ className: 'dropzone' })}
                          style={{
                            width: '100%',
                            height: '100%',
                            padding: '16px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#475467',
                          }}
                        >
                          <input {...getInputProps()} />
                          <Image size={32} style={{ marginBottom: '8px' }} />
                          <p style={{ fontSize: '12px', margin: 0 }}>
                            Drag 'n' drop image here, or click to select
                          </p>
                          <p style={{ fontSize: '10px', marginTop: '4px' }}>
                            JPG, JPEG, PNG
                          </p>
                        </div>
                      </div>
                    )}
                  />
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      textAlign: 'center',
                      width: '100%',
                      minHeight: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={formValues.backgroundImage}
                      alt="Uploaded image"
                      style={{
                        width: '100%',
                        maxHeight: '120px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #E4E7EC',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        },
                      }}
                      onClick={handleRemoveImage}
                    >
                      <Trash size={12} color="white" />
                    </Box>
                  </Box>
                )}
              </div>
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
